import React, { Fragment, useState, useEffect, useContext } from "react";
import styled from "@emotion/styled";
import { hot } from "react-hot-loader/root";
import { Global, css } from "@emotion/core";
import fetch from "unfetch";
import { csvParse, autoType } from "d3-dsv";
import { sum } from "d3-array";
import { numberFormat } from "~/core/scripts/utils";
import { TranslationContext } from "~/kits/general/scripts/app.js";
import { useTheme } from "emotion-theming";

import {
  StoryItem,
  InnerWrapper,
  StickyWrapper,
  StoryItems,
} from "../Explainer/Explainer.styles";

import * as S from "./LastElection.styles.js";

const RestyledStickyWrapper = styled(StickyWrapper)`
  padding-left: 15px;
  padding-right: 15px;

  ${(p) => p.theme.breakpoints.sm.up} {
    padding-left: 25px;
    padding-right: 25px;
  }
`;

function LastElection() {
  const theme = useTheme();
  const polyglot = useContext(TranslationContext);
  const [storyIndex, setStoryIndex] = useState(0);
  const [data, setData] = useState([]);

  const items = [
    {
      content: (
        <div
          dangerouslySetInnerHTML={{ __html: polyglot.t("lastElection1") }}
        />
      ),
    },
    {
      content: (
        <div
          dangerouslySetInnerHTML={{ __html: polyglot.t("lastElection2") }}
        />
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch("data/last-election-results.csv");
      const csv = await response.text();
      const data = csvParse(csv, autoType);

      setData(data);
    };

    fetchData();
  }, []);

  const isPopularVote = storyIndex === 1;

  const trumpSum = sum(
    data.filter((d) => d.candidate === "Trump"),
    (d) => (isPopularVote ? d.votes : d.seats)
  );

  const clintonSum = sum(
    data.filter((d) => d.candidate === "Clinton"),
    (d) => (isPopularVote ? d.votes : d.seats)
  );

  return (
    <S.Wrapper>
      <S.CenterOnDesktop>
        <div className="text-wrap">
          <h3
            dangerouslySetInnerHTML={{
              __html: polyglot.t("lastElectionTitle"),
            }}
          />
          {/* <p
            dangerouslySetInnerHTML={{
              __html: polyglot.t("lastElectionIntro"),
            }}
          /> */}
        </div>
      </S.CenterOnDesktop>
      <InnerWrapper>
        <RestyledStickyWrapper>
          <S.BarsHeader>
            <S.Candidate>
              <S.Head src="images/heads/trump.png" width="189" height="252" />
              <S.Name party="republican">
                Trump{" "}
                <S.ElectedCheck
                  style={{ opacity: isPopularVote ? 0 : 1 }}
                  type="elected-check"
                />
              </S.Name>
            </S.Candidate>
            <S.BarsLabel>
              {isPopularVote
                ? polyglot.t("popularVote")
                : polyglot.t("electoralCollege")}
            </S.BarsLabel>
            <S.Candidate rightAligned>
              <S.Head src="images/heads/clinton.png" width="189" height="252" />
              <S.Name party="democratic">
                <S.ElectedCheck
                  style={{ opacity: isPopularVote ? 1 : 0 }}
                  type="elected-check"
                />{" "}
                Clinton
              </S.Name>
            </S.Candidate>
          </S.BarsHeader>
          <LastElectionBars data={data} isPopularVote={storyIndex === 1} />
          <S.BarsFooter>
            <S.Sum>{numberFormat(trumpSum)}</S.Sum>
            <S.Middle>
              ▴<br />
              50%
            </S.Middle>
            <S.Sum rightAligned>{numberFormat(clintonSum)}</S.Sum>
          </S.BarsFooter>
        </RestyledStickyWrapper>
        <StoryItems>
          {items.map((item, i) => (
            <StoryItem
              key={`story-item-${i}`}
              onEnterViewport={() => setStoryIndex(i)}
              isActive={storyIndex === i}
            >
              {item.content}
            </StoryItem>
          ))}
        </StoryItems>
      </InnerWrapper>
      <Global
        styles={css`
          ._republican_,
          ._trump_ {
            color: ${theme.colors.republican};
            font-weight: bold;
          }
          ._democratic_,
          ._biden_ {
            color: ${theme.colors.democratic};
            font-weight: bold;
          }

          ._trump_,
          ._biden_ {
            position: relative;

            &:after {
              content: "";
              position: absolute;
              display: block;
              width: 37.5px;
              height: 50px;
              background-size: contain;
              background-repeat: no-repeat;
              z-index: -1;
              left: 90%;
              top: 0;
            }
          }
          ._trump_::after {
            background-image: url("images/heads/trump.png");
            transform: translateX(-60%) translateY(-60%) rotate(-10deg);
          }
          ._biden_::after {
            background-image: url("images/heads/biden.png");
            transform: translateY(-30%) rotate(15deg);
          }
          h1 {
            font-family: Roboto;
            font-weight: 700;
            text-shadow: -2px 2px 2px rgba(255, 255, 255, 0.5),
              -2px -2px 2px rgba(255, 255, 255, 0.5),
              2px -2px 2px rgba(255, 255, 255, 0.5),
              2px 2px 2px rgba(255, 255, 255, 0.5);

            span {
              position: relative;
              font-weight: 700 !important;
            }
          }
          @media screen and (max-width: 767px) {
            .second-title {
              font-size: 22px !important;
            }
          }
        `}
      />
    </S.Wrapper>
  );
}

const LastElectionBars = ({ data, isPopularVote }) => {
  return (
    <S.BarsWrapper>
      {data
        .sort((a, b) => b.candidate.localeCompare(a.candidate))
        .map((d, i, a) => (
          <Fragment key={`bar-${i}`}>
            <S.Bar
              isPopularVote={isPopularVote}
              width={isPopularVote ? d.votes : d.seats}
              candidate={d.candidate}
            />
            {i + 1 < a.length && d.candidate !== a[i + 1].candidate && (
              <div key="separator" style={{ minWidth: 2 }} />
            )}
          </Fragment>
        ))}
    </S.BarsWrapper>
  );
};

export default hot(LastElection);
